import { useMemo, useRef, useState } from 'react';
import type { Attachment } from '@orbit-sync/shared-types';
import { formatBytes, formatTime } from '../chat/labels.js';
import { useUploadStore } from '../files/uploadStore.js';
import { api, errorMessage, isApiRequestError } from '../lib/api/index.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { Icon } from './Icon.js';

/** The paperclip: picks one or more files and starts uploading them at once. */
export function AttachButton({ channelId }: { channelId: string }) {
  const start = useUploadStore((state) => state.start);
  const max = useSettingsStore((state) => state.settings.maxFileTransferBytes);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        className="btn btn--icon"
        aria-label="Attach files"
        title={`Attach files (up to ${formatBytes(max)} each) - or drop them on the conversation`}
        onClick={() => inputRef.current?.click()}
      >
        <Icon name="paperclip" size={18} />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          // Cleared, so choosing the same file again still fires `change`.
          event.target.value = '';

          if (files.length > 0) {
            start(channelId, files);
          }
        }}
      />
    </>
  );
}

/** Uploads in flight (or failed) for one conversation, above the composer. */
export function UploadTray({ channelId }: { channelId: string }) {
  const all = useUploadStore((state) => state.uploads);
  const cancel = useUploadStore((state) => state.cancel);
  const uploads = useMemo(
    () => all.filter((upload) => upload.channelId === channelId),
    [all, channelId],
  );

  if (uploads.length === 0) {
    return null;
  }

  return (
    <ul className="upload-tray" aria-label="Uploads">
      {uploads.map((upload) => (
        <li
          key={upload.id}
          className={upload.status === 'failed' ? 'upload upload--failed' : 'upload'}
        >
          <Icon name={upload.status === 'failed' ? 'alertCircle' : 'upload'} size={15} />
          <span className="upload-body">
            <span className="upload-name truncate" dir="auto">
              {upload.name}
            </span>
            {upload.status === 'failed' ? (
              <span className="upload-meta upload-meta--error">{upload.error}</span>
            ) : (
              <span className="upload-meta">
                {formatBytes(Math.round(upload.size * upload.progress))} of{' '}
                {formatBytes(upload.size)}
              </span>
            )}
            {upload.status === 'uploading' ? (
              <span className="upload-bar" aria-hidden="true">
                <span style={{ width: `${Math.round(upload.progress * 100)}%` }} />
              </span>
            ) : null}
          </span>
          <button
            type="button"
            className="btn btn--icon btn--sm"
            aria-label={upload.status === 'failed' ? 'Dismiss' : 'Cancel upload'}
            onClick={() => cancel(upload.id)}
          >
            <Icon name="x" size={14} />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * A shared file in the transcript. Anyone in the conversation can download it
 * until it expires - or until every recipient has, when the server lets it go.
 */
export function FileCard({ attachment }: { attachment: Attachment }) {
  const [state, setState] = useState<'idle' | 'busy' | 'gone' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  // Read once, when the card appears, rather than kept live: a ticking clock
  // per file is not worth it, and a click on an expired file says so anyway.
  const [openedAt] = useState(() => Date.now());
  const expired = new Date(attachment.expiresAt).getTime() <= openedAt;
  const unavailable = expired || state === 'gone';

  async function download(): Promise<void> {
    setState('busy');
    setError(null);

    try {
      await api.files.downloadAttachment(attachment);
      setState('idle');
    } catch (cause) {
      const gone = isApiRequestError(cause) && cause.status === 410;
      setState(gone ? 'gone' : 'error');
      setError(gone ? null : errorMessage(cause));
    }
  }

  return (
    <span className="file-card">
      <span className="file-card-icon">
        <Icon name="file" size={18} />
      </span>
      <span className="file-card-body">
        <span className="file-card-name" dir="auto">
          {attachment.name}
        </span>
        <span className="file-card-meta">
          {formatBytes(attachment.size)}
          {' · '}
          {unavailable
            ? 'No longer available'
            : `Available until ${formatTime(attachment.expiresAt)}`}
        </span>
        {error ? <span className="file-card-error">{error}</span> : null}
      </span>
      {unavailable ? null : (
        <button
          type="button"
          className="btn btn--secondary btn--icon btn--sm"
          aria-label={`Download ${attachment.name}`}
          title="Download"
          disabled={state === 'busy'}
          onClick={() => void download()}
        >
          {state === 'busy' ? <span className="btn-spinner" /> : <Icon name="download" size={15} />}
        </button>
      )}
    </span>
  );
}
